// Generic typed fetch helper for the DCC ERP REST API.
// Dev: Vite proxies /api → http://localhost:3100 (see vite.config.ts).
// Prod: same-origin reverse proxy is expected, so relative URLs work in both envs.

import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { ENTRA_API_SCOPE, msalInstance } from './auth/msal-config';
import { saveCurrentPathForReturn } from './auth/return-path';

export const API_BASE = '/api/v1';

// Mirrors the RFC 7807 problem-details shape emitted by AllExceptionsFilter on the
// API side. Fields beyond `status` are best-effort: a non-JSON error body still
// yields a usable HttpError.
export interface ApiError {
  status: number;
  type?: string;
  title?: string;
  detail?: string;
  instance?: string;
}

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.detail ?? body.title ?? `HTTP ${status}`);
    this.name = 'HttpError';
  }
}

// Acquire an access token for the API scope, silently when possible. If MSAL
// signals that an interactive step is required, kick off a redirect — the
// caller will receive an empty header set and the in-flight request will fail,
// but the user will be navigated to Entra to re-authenticate.
async function authHeader(): Promise<Record<string, string>> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return {};
  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: [ENTRA_API_SCOPE],
    });
    return { Authorization: `Bearer ${result.accessToken}` };
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Preserve the current page across the interactive re-auth bounce.
      saveCurrentPathForReturn();
      void msalInstance.acquireTokenRedirect({ scopes: [ENTRA_API_SCOPE] });
    }
    return {};
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // FormData bodies must let the browser set the multipart boundary; forcing
  // application/json here would corrupt the upload, so exempt them.
  const isFormData = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init?.body != null && !isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(await authHeader()),
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  if (res.status === 401) {
    // Token rejected — clear session and bounce to login.
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  if (!res.ok) {
    let body: ApiError = { status: res.status };
    try {
      const parsed = (await res.json()) as Partial<ApiError>;
      body = { ...body, ...parsed };
    } catch {
      // response might not be JSON (e.g. proxy/network error)
    }
    throw new HttpError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function apiJson<TBody, TResp>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: TBody,
): Promise<TResp> {
  return api<TResp>(path, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

// M7-P3 (SoD-by-actorId): merge the optional audited override reason into an
// engine approve/reject/return body. Single helper so every decision hook
// threads `sodOverrideReason` identically (and never sends an empty key when
// absent — the API schema rejects whitespace-only). The engine gates the
// override on the reason being present AND the actor holding
// `approval.sodoverride`; the field is inert for normal (non-deadlocked) flows.
export function withSodOverride<TBody extends object>(
  body: TBody,
  sodOverrideReason?: string,
): TBody & { sodOverrideReason?: string } {
  return sodOverrideReason ? { ...body, sodOverrideReason } : body;
}

// Authenticated binary download. A bare <a href> can't carry the bearer token,
// so we fetch the protected endpoint WITH the Authorization header, read the
// body as a blob, and trigger a browser download via a temporary object URL.
// The filename is taken from the response's Content-Disposition when present,
// else `fallbackFilename`. Reuses the same MSAL token helper as `api`.
export async function downloadFile(path: string, fallbackFilename: string): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { ...(await authHeader()) },
  });
  if (res.status === 401) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  if (!res.ok) {
    let body: ApiError = { status: res.status };
    try {
      const parsed = (await res.json()) as Partial<ApiError>;
      body = { ...body, ...parsed };
    } catch {
      // non-JSON error body (e.g. proxy/network error)
    }
    throw new HttpError(res.status, body);
  }

  const blob = await res.blob();
  const filename =
    filenameFromDisposition(res.headers.get('content-disposition')) ?? fallbackFilename;

  downloadBlob(blob, filename);
}

// Trigger a browser "save" for an in-memory Blob: mint a temporary object URL,
// click a hidden <a download>, then revoke the URL. Used by downloadFile and by
// callers that already have a Blob (e.g. POST-then-download flows via apiBlob,
// since downloadFile is GET-only).
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Binary/text download sibling of api(): same base URL + bearer auth, but
// returns the raw body as a Blob (for PDF / RD-TXT file downloads). Mirrors
// api()'s 401 bounce + RFC-7807 error handling. Kept in this file so it can
// reuse the module-private authHeader()/API_BASE without exporting internals.
export async function apiBlob(path: string, init?: RequestInit): Promise<Blob> {
  const headers: Record<string, string> = {
    ...(await authHeader()),
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
  if (!res.ok) {
    let body: ApiError = { status: res.status };
    try {
      const parsed = (await res.json()) as Partial<ApiError>;
      body = { ...body, ...parsed };
    } catch {
      // non-JSON error body (proxy/network) — keep the status-only body
    }
    throw new HttpError(res.status, body);
  }
  return res.blob();
}

// Pull a filename out of a Content-Disposition header
// (e.g. `attachment; filename="statement-C-0001-20260603.pdf"`). Returns null
// when absent so the caller can fall back.
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}
